import { FC, useMemo, useRef } from 'react'
import './account-limits-banner.css'

import * as Icons from '@icons'
import Button from '@components/atoms/button'
import Card from '@components/atoms/card'
import Divider from '@components/atoms/divider'
import DashboardBanner from '@components/molecules/dashboard-banner'
import { getRestrictedUserLimitDetails, parseUserLimits } from '@schemas'

interface Props {
    limits?: Partial<UserLimits> | null
}

const AccountLimitsBanner: FC<Props> = ({ limits: rawLimits }) => {
    const dialogRef = useRef<HTMLDialogElement>(null)
    const limits = useMemo(() => parseUserLimits(rawLimits), [rawLimits])
    const restrictedDetails = useMemo(() => getRestrictedUserLimitDetails(limits), [limits])

    if (restrictedDetails.length < 1) return null

    function openDialog() {
        dialogRef.current?.showModal()
    }

    function closeDialog() {
        dialogRef.current?.close()
    }

    return (
        <>
            <DashboardBanner variant='limitedAccount' onClick={openDialog} />
            <dialog ref={dialogRef}>
                <div>
                    <div className='account-limits-dialog'>
                        <div className='account-limits-dialog-header'>
                            <div className='account-limits-dialog-header-copy'>
                                <div
                                    className='bubble-sml flex items-center justify-center shrink-0'
                                    style={{ background: 'var(--splitcard-orange-100, #FFEDD5)' }}
                                >
                                    <Icons.Lock tint='var(--splitcard-orange-500, #F97316)' width='1.25rem' height='1.25rem' />
                                </div>
                                <div>
                                    <h2>Account Limits</h2>
                                    <p className='text-body text-gray-500'>Some actions have been limited on your account.</p>
                                </div>
                            </div>
                            <button className='account-limits-dialog-close' type='button' onClick={closeDialog} aria-label='Close dialog'>
                                <Icons.Close width='1rem' height='1rem' tint='var(--splitcard-gray-500)' />
                            </button>
                        </div>

                        <Card className='account-limits-dialog-card'>
                            {restrictedDetails.map((detail, index) => (
                                <div key={detail.key}>
                                    {index > 0 && <Divider />}
                                    <div className='account-limits-dialog-row'>
                                        <p className='text-body'><strong>{detail.label}</strong></p>
                                    </div>
                                </div>
                            ))}
                        </Card>

                        <Card style={{ textAlign: 'center' }}>
                            <p className='body2'><strong>Contact Support</strong></p>
                            <div className='flex flex-row items-center justify-center gap-2'>
                                <Icons.Phone tint='var(--splitcard-blue-500)' height={'1rem'} width={'1rem'} />
                                <a onClick={() => window.open("tel:+18886254349")}>888-625-4349</a>
                            </div>
                            <div className='flex flex-row items-center justify-center gap-2'>
                                <Icons.Email tint='var(--splitcard-blue-500)' height={'1rem'} width={'1rem'} />
                                <a onClick={() => window.open("mailto:support@splitcard.com")}>support@splitcard.com</a>
                            </div>
                        </Card>

                        <div className='mt-4'>
                            <Button className='w-full' theme='primary' onClick={closeDialog}>Got It!</Button>
                        </div>
                    </div>
                </div>
            </dialog>
        </>
    )
}

export default AccountLimitsBanner
